/**
 * Motorcycle Garage
 * 
 * Allows customers to save their motorcycles for quick filtering.
 * Uses localStorage for guest users and Shopify customer metafields for logged-in users.
 */

class MotorcycleGarage {
  constructor() {
    this.STORAGE_KEY = 'motorcycleGarage';
    this.MAX_BIKES = 5;
    this.API_VERSION = '2024-01';
  }

  /**
   * Get all bikes from garage
   */
  async getBikes() {
    if (window.Shopify?.customerPrivacy?.currentVisitorConsent()?.marketing) {
      // Logged in - try to get from Shopify customer metafields
      const shopifyBikes = await this.getBikesFromShopify();
      if (shopifyBikes) {
        return shopifyBikes;
      }
    }
    
    // Guest or fallback - use localStorage
    return this.getBikesFromStorage();
  }

  /**
   * Get bikes from localStorage
   */
  getBikesFromStorage() {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error reading garage from localStorage:', error);
      return [];
    }
  }

  /**
   * Get bikes from Shopify customer metafields
   */
  async getBikesFromShopify() {
    // Check if customer is logged in
    if (!window.Shopify?.routes?.root) {
      return null;
    }

    try {
      const response = await fetch('/account/garage.json', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.bikes || [];
      }
    } catch (error) {
      console.error('Error fetching garage from Shopify:', error);
    }

    return null;
  }

  /**
   * Save bikes to garage
   */
  async saveBikes(bikes) {
    // Limit to MAX_BIKES
    const limitedBikes = bikes.slice(0, this.MAX_BIKES);
    
    // Save to localStorage
    this.saveBikesToStorage(limitedBikes);
    
    // If logged in, save to Shopify
    if (window.Shopify?.routes?.root) {
      await this.saveBikesToShopify(limitedBikes);
    }
    
    // Dispatch event for other components
    window.dispatchEvent(new CustomEvent('garageUpdated', {
      detail: { bikes: limitedBikes }
    }));
    
    return limitedBikes;
  }

  /**
   * Save bikes to localStorage
   */
  saveBikesToStorage(bikes) {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(bikes));
    } catch (error) {
      console.error('Error saving garage to localStorage:', error);
    }
  }

  /**
   * Save bikes to Shopify customer metafields
   */
  async saveBikesToShopify(bikes) {
    try {
      const response = await fetch('/account/garage', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ bikes })
      });

      if (!response.ok) {
        console.error('Error saving garage to Shopify');
      }
    } catch (error) {
      console.error('Error saving garage to Shopify:', error);
    }
  }

  /**
   * Add a bike to garage
   */
  async addBike(year, make, model) {
    const bikes = await this.getBikes();
    
    // Check if bike already exists
    const exists = bikes.some(bike => 
      bike.year === year && 
      bike.make === make && 
      bike.model === model
    );
    
    if (exists) {
      throw new Error('This bike is already in your garage');
    }
    
    // Check max bikes limit
    if (bikes.length >= this.MAX_BIKES) {
      throw new Error(`You can only save up to ${this.MAX_BIKES} motorcycles`);
    }
    
    // Add bike
    const newBike = {
      year,
      make,
      model,
      addedAt: new Date().toISOString(),
      nickname: this.generateNickname(year, make, model)
    };
    
    bikes.push(newBike);
    
    await this.saveBikes(bikes);
    
    // Track analytics
    this.trackGarageEvent('bike_added', newBike);
    
    return newBike;
  }

  /**
   * Remove a bike from garage
   */
  async removeBike(index) {
    const bikes = await this.getBikes();
    
    if (index < 0 || index >= bikes.length) {
      throw new Error('Invalid bike index');
    }
    
    const removedBike = bikes[index];
    bikes.splice(index, 1);
    
    await this.saveBikes(bikes);
    
    // Track analytics
    this.trackGarageEvent('bike_removed', removedBike);
    
    return removedBike;
  }

  /**
   * Update bike nickname
   */
  async updateNickname(index, nickname) {
    const bikes = await this.getBikes();
    
    if (index < 0 || index >= bikes.length) {
      throw new Error('Invalid bike index');
    }
    
    bikes[index].nickname = nickname;
    await this.saveBikes(bikes);
    
    return bikes[index];
  }

  /**
   * Get a specific bike
   */
  async getBike(index) {
    const bikes = await this.getBikes();
    return bikes[index] || null;
  }

  /**
   * Generate a default nickname
   */
  generateNickname(year, make, model) {
    return `${year} ${make} ${model}`;
  }

  /**
   * Clear entire garage
   */
  async clearGarage() {
    await this.saveBikes([]);
    
    // Track analytics
    this.trackGarageEvent('garage_cleared', {});
  }

  /**
   * Track garage events for analytics
   */
  trackGarageEvent(eventName, data) {
    // Dispatch custom event
    window.dispatchEvent(new CustomEvent('motorcycleGarageEvent', {
      detail: {
        event: eventName,
        data: data,
        timestamp: new Date().toISOString()
      }
    }));

    // Send to analytics endpoint
    if (window.motorcycleAnalytics) {
      window.motorcycleAnalytics.track(eventName, data);
    }

    // Google Analytics 4
    if (typeof gtag !== 'undefined') {
      gtag('event', eventName, {
        event_category: 'Motorcycle Garage',
        year: data.year,
        make: data.make,
        model: data.model
      });
    }

    // Shopify Analytics
    if (window.ShopifyAnalytics) {
      window.ShopifyAnalytics.lib.track('Motorcycle Garage Action', {
        action: eventName,
        ...data
      });
    }
  }

  /**
   * Sync localStorage to Shopify (for when user logs in)
   */
  async syncToShopify() {
    const localBikes = this.getBikesFromStorage();
    if (localBikes.length > 0) {
      await this.saveBikesToShopify(localBikes);
    }
  }

  /**
   * Import bikes from array
   */
  async importBikes(bikes) {
    const validBikes = bikes.filter(bike => 
      bike.year && bike.make && bike.model
    ).slice(0, this.MAX_BIKES);
    
    await this.saveBikes(validBikes);
    
    return validBikes;
  }

  /**
   * Export bikes as JSON
   */
  async exportBikes() {
    const bikes = await this.getBikes();
    return JSON.stringify(bikes, null, 2);
  }
}

// Create global instance
window.motorcycleGarage = new MotorcycleGarage();

// Custom element for garage widget
if (!customElements.get('motorcycle-garage-widget')) {
  class MotorcycleGarageWidget extends HTMLElement {
    constructor() {
      super();
      this.garage = window.motorcycleGarage;
      this.bikes = [];
    }

    async connectedCallback() {
      await this.render();
      this.setupEventListeners();
      
      // Listen for garage updates
      window.addEventListener('garageUpdated', () => {
        this.render();
      });
    }

    async render() {
      this.bikes = await this.garage.getBikes();
      
      const isEmpty = this.bikes.length === 0;
      
      this.innerHTML = `
        <div class="motorcycle-garage-widget">
          <div class="motorcycle-garage-header">
            <h3 class="motorcycle-garage-title">My Garage</h3>
            ${this.bikes.length > 0 ? `
              <button type="button" class="button-text-link garage-clear-all">
                Clear All
              </button>
            ` : ''}
          </div>
          
          ${isEmpty ? this.renderEmpty() : this.renderBikes()}
          
          ${this.bikes.length < this.garage.MAX_BIKES ? `
            <button type="button" class="button button-secondary garage-add-bike">
              <span class="icon">+</span> Add Motorcycle
            </button>
          ` : ''}
        </div>
      `;
    }

    renderEmpty() {
      return `
        <div class="motorcycle-garage-empty">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke-width="2" stroke-linecap="round"/>
          </svg>
          <p>No motorcycles saved yet</p>
          <p class="small">Save your bikes for quick part filtering</p>
        </div>
      `;
    }

    renderBikes() {
      return `
        <div class="motorcycle-garage-list">
          ${this.bikes.map((bike, index) => `
            <div class="motorcycle-garage-item" data-index="${index}">
              <div class="garage-item-header">
                <span class="garage-item-year">${bike.year}</span>
                <button type="button" class="garage-item-remove" data-index="${index}" aria-label="Remove bike">
                  ×
                </button>
              </div>
              <div class="garage-item-make">${bike.make}</div>
              <div class="garage-item-model">${bike.model}</div>
              <div class="garage-item-actions">
                <button type="button" class="button button-small garage-item-select" data-index="${index}">
                  Shop Parts
                </button>
                <button type="button" class="button-text-link garage-item-edit" data-index="${index}">
                  Edit
                </button>
              </div>
            </div>
          `).join('')}
        </div>
      `;
    }

    setupEventListeners() {
      // Add bike button
      this.querySelector('.garage-add-bike')?.addEventListener('click', () => {
        this.showAddBikeModal();
      });

      // Clear all button
      this.querySelector('.garage-clear-all')?.addEventListener('click', async () => {
        if (confirm('Are you sure you want to remove all motorcycles from your garage?')) {
          await this.garage.clearGarage();
          this.render();
        }
      });

      // Remove bike buttons
      this.querySelectorAll('.garage-item-remove').forEach(button => {
        button.addEventListener('click', async (e) => {
          const index = parseInt(e.target.dataset.index);
          await this.garage.removeBike(index);
          this.render();
        });
      });

      // Select bike buttons
      this.querySelectorAll('.garage-item-select').forEach(button => {
        button.addEventListener('click', async (e) => {
          const index = parseInt(e.target.dataset.index);
          const bike = await this.garage.getBike(index);
          
          if (bike) {
            // Navigate to filter with this bike selected
            const tags = motorcycleDatabase.getTags(bike.make, bike.model, bike.year);
            const collectionUrl = this.dataset.collectionUrl || window.routes?.all_products_url || '/collections/all';
            const path = `${collectionUrl}/bike:${bike.make.toLowerCase().replace(/\s+/g, '-')}-${bike.model.toLowerCase().replace(/\s+/g, '-')}-${bike.year}`;
            window.location.href = path;
          }
        });
      });

      // Edit bike buttons
      this.querySelectorAll('.garage-item-edit').forEach(button => {
        button.addEventListener('click', async (e) => {
          const index = parseInt(e.target.dataset.index);
          const bike = await this.garage.getBike(index);
          
          if (bike) {
            const newNickname = prompt('Enter a nickname for this bike:', bike.nickname);
            if (newNickname && newNickname !== bike.nickname) {
              await this.garage.updateNickname(index, newNickname);
              this.render();
            }
          }
        });
      });
    }

    showAddBikeModal() {
      // Dispatch event to open the motorcycle filter modal
      window.dispatchEvent(new CustomEvent('openGarageAddModal'));
    }
  }

  customElements.define('motorcycle-garage-widget', MotorcycleGarageWidget);
}

// Sync garage when user logs in
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (window.Shopify?.routes?.root) {
      window.motorcycleGarage.syncToShopify();
    }
  });
} else {
  if (window.Shopify?.routes?.root) {
    window.motorcycleGarage.syncToShopify();
  }
}

